import React, { useMemo, useState, useRef } from "react";

interface SpecializationTagInputProps {
  value:        string[];
  onChange:     (tags: string[]) => void;
  placeholder?: string;
  error?:       string;
  label?:       string;
  /** Locks the field (e.g. while a card is not in edit mode). */
  disabled?:    boolean;
  /**
   * Known values to suggest as-you-type (case-insensitive prefix match),
   * e.g. a clinic's existing patient tags. Optional and generic — this
   * component is also used for doctor specializations, which pass nothing.
   */
  suggestions?: string[];
}

export const SpecializationTagInput: React.FC<SpecializationTagInputProps> = ({
  value,
  onChange,
  placeholder = "e.g. Cardiologist",
  error,
  label,
  disabled = false,
  suggestions,
}) => {
  const [inputText, setInputText] = useState("");
  const [showSuggestions, setShowSuggestions] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // ── Core logic ──────────────────────────────────────────────────────────
  // Case-insensitive dedup: "Urgent" and "urgent" are the same tag.
  const hasTag = (list: string[], candidate: string) =>
    list.some((t) => t.toLowerCase() === candidate.toLowerCase());

  const commitTag = (raw?: string) => {
    const clean = (raw ?? inputText).trim().replace(/,+$/, ""); // strip trailing commas
    setShowSuggestions(false);
    if (!clean) return;
    if (hasTag(value, clean)) { setInputText(""); return; } // no duplicates
    onChange([...value, clean]);
    setInputText("");
  };

  const removeTag = (index: number) => {
    onChange(value.filter((_, i) => i !== index));
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "," || e.key === "Enter") {
      e.preventDefault(); // stop comma appearing in the input box
      commitTag();
    }
    if (e.key === "Escape") {
      setShowSuggestions(false);
    }
    // Backspace on empty input removes the last tag — quality of life
    if (e.key === "Backspace" && inputText === "") {
      onChange(value.slice(0, -1));
    }
  };

  const filteredSuggestions = useMemo(() => {
    if (!suggestions || !inputText.trim()) return [];
    const q = inputText.trim().toLowerCase();
    return suggestions
      .filter((s) => s.toLowerCase().startsWith(q) && !hasTag(value, s))
      .slice(0, 6);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [suggestions, inputText, value]);

  // ── Render ───────────────────────────────────────────────────────────────

  return (
    <div className="flex flex-col gap-1">

      {label && (
        <label className="text-xs font-medium text-text-muted">{label}</label>
      )}

      {/*
        Container acts as the visual input box.
        Clicking anywhere inside focuses the real <input> below.
      */}
      <div className="relative">
        <div
          onClick={() => { if (!disabled) inputRef.current?.focus(); }}
          className={`
            flex flex-wrap gap-1.5 min-h-[42px] w-full
            rounded-lg border px-3 py-2
            ${disabled
              ? "cursor-not-allowed bg-gray-50 border-border"
              : error
                ? "cursor-text bg-surface border-danger focus-within:ring-1 focus-within:ring-danger"
                : "cursor-text bg-surface border-border focus-within:border-brand focus-within:ring-1 focus-within:ring-brand"
            }
          `}
        >
          {/* Committed tags rendered as chips */}
          {value.map((tag, index) => (
            <span
              key={index}
              className="inline-flex items-center gap-1 rounded-full bg-brand-light px-2.5 py-0.5 text-xs font-medium text-brand"
            >
              {tag}
              {!disabled && (
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); removeTag(index); }}
                  className="ml-0.5 leading-none text-brand hover:text-brand-dark"
                  aria-label={`Remove ${tag}`}
                >
                  ×
                </button>
              )}
            </span>
          ))}

          {/* Actual text input — no border, sits inline with chips */}
          {!disabled && (
            <input
              ref={inputRef}
              type="text"
              value={inputText}
              onChange={(e) => { setInputText(e.target.value); setShowSuggestions(true); }}
              onFocus={() => setShowSuggestions(true)}
              onKeyDown={handleKeyDown}
              onBlur={() => commitTag()}
              placeholder={value.length === 0 ? placeholder : ""}
              className="min-w-[120px] flex-1 bg-transparent text-sm text-text outline-none placeholder:text-text-muted"
            />
          )}
        </div>

        {!disabled && showSuggestions && filteredSuggestions.length > 0 && (
          <ul className="absolute z-10 mt-1 w-full max-h-40 overflow-y-auto rounded-lg border border-border bg-surface shadow-md">
            {filteredSuggestions.map((s) => (
              <li key={s}>
                <button
                  type="button"
                  onMouseDown={(e) => e.preventDefault()} // keep input focused so blur doesn't fire first
                  onClick={() => { commitTag(s); inputRef.current?.focus(); }}
                  className="block w-full truncate px-3 py-1.5 text-left text-sm text-text hover:bg-brand-light"
                >
                  {s}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {error ? (
        <p className="mt-0.5 text-xs text-danger">{error}</p>
      ) : !disabled ? (
        <p className="mt-0.5 text-xs text-text-muted">
          Press comma or Enter to add. Click × to remove.
        </p>
      ) : null}

    </div>
  );
};